import { c as _c } from "react/compiler-runtime";
// Общие детали Единого партнёрского кабинета (/partner, ТЗ 31.10.2026):
// подписи каналов и статусов, копирование в буфер, CSV пула артикулов,
// бейджи и карточки в визуальном языке витрины (#F8F8F8 r20, Manrope).
import { UgcChannelSchema } from "@osmix/contracts/ugc-articles";
import { Copy } from "lucide-react";
import type * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { UgcArticle, UgcArticleStatus, UgcChannel, UgcCommissionState } from "@/api";
import { cn } from "@/lib/utils";
export const UGC_CHANNELS = UgcChannelSchema.options as readonly UgcChannel[];
export const partnerKey = (userId: string | undefined, ...rest: unknown[]) => ["partner", userId ?? "guest", ...rest];

/** Скопировать текст: Clipboard API, без него - старый execCommand. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const area = document.createElement("textarea");
      area.value = text;
      area.setAttribute("readonly", "");
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand("copy");
      area.remove();
      return ok;
    } catch {
      return false;
    }
  }
}
export function useCopy() {
  const $ = _c(2);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] !== t) {
    t0 = async (text, done) => {
      const ok = await copyText(text);
      if (ok) {
        toast.success(done ?? t("partner.copied"));
      } else {
        toast.error(t("partner.copyFailed"));
      }
    };
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
const csvCell = (value: string) => /[",;\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/** Пул артикулов в CSV: код, источник, площадка, кампания, товар. */
export const articlesCsv = (list: UgcArticle[], header: string[]): string => [header, ...list.map(a => [a.code, a.name, a.placement, a.campaign, a.productTitle])].map(row => row.map(csvCell).join(";")).join("\n");
export function downloadCsv(name: string, csv: string): void {
  try {
    // BOM - чтобы Excel открыл кириллицу без «кракозябр».
    const blob = new Blob([`﻿${csv}`], {
      type: "text/csv;charset=utf-8"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    toast.error("Не удалось скачать файл");
  }
}
const STATUS_TONE: Record<UgcArticleStatus, string> = {
  moderation: "bg-[#FFF4D6] text-[#8A5A00]",
  active: "bg-[#E6F6EA] text-[#1E7B3A]",
  disabled: "bg-ds-gray text-ds-secondary",
  blocked: "bg-[#FDECEC] text-[#C2362F]",
  deleted: "bg-ds-gray text-ds-secondary"
};
export function StatusBadge(t0) {
  const $ = _c(8);
  const {
    status
  } = t0;
  const {
    t
  } = useTranslation();
  const t1 = STATUS_TONE[status];
  let t2;
  if ($[0] !== t1) {
    t2 = cn("inline-flex h-6 shrink-0 items-center rounded-full px-2.5 text-[12px] leading-4 font-semibold whitespace-nowrap", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `partner.status.${status}`;
  let t4;
  if ($[2] !== t || $[3] !== t3) {
    t4 = t(t3);
    $[2] = t;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <span className={t2}>{t4}</span>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
const STATE_TONE: Record<UgcCommissionState, string> = {
  hold: "bg-[#FFF4D6] text-[#8A5A00]",
  available: "bg-[#E6F6EA] text-[#1E7B3A]",
  paid: "bg-[#E8F1FF] text-[#1F5FBF]",
  cancelled: "bg-ds-gray text-ds-secondary"
};
export function CommissionStateBadge(t0) {
  const $ = _c(8);
  const {
    state
  } = t0;
  const {
    t
  } = useTranslation();
  const t1 = STATE_TONE[state];
  let t2;
  if ($[0] !== t1) {
    t2 = cn("inline-flex h-6 shrink-0 items-center rounded-full px-2.5 text-[12px] leading-4 font-semibold whitespace-nowrap", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `partner.stats.state.${state}`;
  let t4;
  if ($[2] !== t || $[3] !== t3) {
    t4 = t(t3);
    $[2] = t;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <span className={t2}>{t4}</span>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}

/** Карточка раздела: плашка #F8F8F8 r20 на белой странице, как панели профиля. */
export function PartnerCard(t0) {
  const $ = _c(5);
  const {
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("rounded-[20px] bg-ds-quaternary p-4 md:p-5", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/** Код артикула с кнопкой копирования - моноширинно, чтобы цифры не прыгали. */
export function CodeChip(t0) {
  const $ = _c(13);
  const {
    code,
    className
  } = t0;
  const {
    t
  } = useTranslation();
  const copy = useCopy();
  let t1;
  if ($[0] !== code || $[1] !== copy) {
    t1 = () => void copy(code);
    $[0] = code;
    $[1] = copy;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t("partner.generate.copyCode");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== className) {
    t3 = cn("inline-flex h-7 shrink-0 items-center gap-1.5 rounded-[8px] bg-white px-2 shadow-[inset_0_0_0_1px_var(--ds-line-2,#EAEAEB)] font-mono text-[13px] leading-4 font-semibold tracking-[0.04em] text-ds-primary outline-none transition-colors hover:bg-ds-gray-hover focus-visible:ring-2 focus-visible:ring-ds-focus", className);
    $[5] = className;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Copy className="size-3.5 text-ds-secondary" aria-hidden="true" />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== code || $[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t5 = <button type="button" onClick={t1} title={t2} className={t3}>{code}{t4}</button>;
    $[8] = code;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
export function EmptyNote(t0) {
  const $ = _c(5);
  const {
    children,
    action
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <p className="max-w-[440px] text-[14px] leading-[18px] font-medium text-ds-secondary">{children}</p>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== action || $[3] !== t1) {
    t2 = <div className="flex flex-col items-center gap-3 rounded-[20px] bg-ds-quaternary px-5 py-10 text-center">{t1}{action}</div>;
    $[2] = action;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/** Целые числа и проценты по-русски: «1 234», «3,5 %». */
export const fmtInt = (n: number) => new Intl.NumberFormat("ru-RU").format(Math.round(n));
export const fmtPercent = (n: number) => `${new Intl.NumberFormat("ru-RU", {
  maximumFractionDigits: 1
}).format(n)} %`;
export const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("ru-RU");
};